import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  subscribeToPush, unsubscribeFromPush,
  isCurrentlySubscribed, isPushSupported, getPushPermission
} from '../lib/pushNotifications';
import {
  Bell, BellOff, Save, Loader2, AlertTriangle, Target,
  Lightbulb, Zap, Phone, FileText, Calendar, TrendingDown,
  Check, X, Info
} from 'lucide-react';

function Toggle({ checked, onChange, testId }) {
  return (
    <button onClick={() => onChange(!checked)} data-testid={testId} className="relative shrink-0">
      {checked ? (
        <div className="w-10 h-[22px] rounded-full bg-blue-500 relative transition-colors">
          <div className="w-4 h-4 bg-white rounded-full absolute right-0.5 top-[3px] transition-all" />
        </div>
      ) : (
        <div className="w-10 h-[22px] rounded-full relative transition-colors" style={{ backgroundColor: 'var(--border-color)' }}>
          <div className="w-4 h-4 bg-white rounded-full absolute left-0.5 top-[3px] transition-all" />
        </div>
      )}
    </button>
  );
}

function AlertRow({ icon: Icon, color, label, description, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between py-3 px-4 rounded-lg transition-colors"
      style={{ backgroundColor: checked ? color + '08' : 'transparent' }}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: color + '20' }}>
          <Icon size={15} style={{ color }} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{label}</p>
          <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{description}</p>
        </div>
      </div>
      <Toggle checked={checked} onChange={onChange} testId={testId} />
    </div>
  );
}

export default function NotificationPrefsPage() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushSupported, setPushSupported] = useState(true);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [prefsData, supported, subscribed, permission] = await Promise.all([
        api.get('/api/notifications/preferences'),
        isPushSupported(),
        isCurrentlySubscribed(),
        getPushPermission(),
      ]);
      setPrefs(prefsData);
      setPushSupported(supported);
      setPushSubscribed(subscribed);
      setPushPermission(permission);
    } catch (err) { toast.error('Error cargando preferencias'); }
    setLoading(false);
  };

  const updatePref = (key, value) => {
    setPrefs(prev => ({ ...prev, [key]: value }));
  };

  const savePrefs = async () => {
    setSaving(true);
    try {
      await api.put('/api/notifications/preferences', prefs);
      toast.success('Preferencias guardadas');
    } catch (err) { toast.error('Error al guardar'); }
    setSaving(false);
  };

  const activatePush = async () => {
    setActivating(true);
    try {
      await subscribeToPush();
      setPushSubscribed(true);
      setPushPermission('granted');
      updatePref('enabled', true);
      // Also save enabled=true to backend
      await api.put('/api/notifications/preferences', { enabled: true });
      toast.success('Notificaciones push activadas');
    } catch (err) {
      toast.error(err.message || 'Error al activar notificaciones');
      if (Notification.permission === 'denied') setPushPermission('denied');
    }
    setActivating(false);
  };

  const deactivatePush = async () => {
    setActivating(true);
    try {
      await unsubscribeFromPush();
      setPushSubscribed(false);
      updatePref('enabled', false);
      await api.put('/api/notifications/preferences', { enabled: false });
      toast.success('Notificaciones push desactivadas');
    } catch (err) { toast.error('Error al desactivar'); }
    setActivating(false);
  };

  if (loading || !prefs) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin" style={{ color: 'var(--accent-primary)' }} />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-3xl" data-testid="notification-prefs-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Notificaciones</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Configura que alertas quieres recibir en tu navegador</p>
      </div>

      {/* Push activation */}
      <div className="rounded-xl border p-5 mb-6" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {prefs.enabled && pushSubscribed ? (
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <Bell size={18} className="text-green-400" />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--surface-secondary)' }}>
                <BellOff size={18} style={{ color: 'var(--text-muted)' }} />
              </div>
            )}
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                {prefs.enabled && pushSubscribed ? 'Notificaciones activas' : 'Notificaciones desactivadas'}
              </p>
              <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                {!pushSupported ? 'Tu navegador no soporta notificaciones push' :
                  pushPermission === 'denied' ? 'Has bloqueado las notificaciones. Desbloquealo en los ajustes del navegador.' :
                  prefs.enabled && pushSubscribed ? 'Recibiras alertas segun tu configuracion' :
                  'Activa para recibir alertas en tiempo real'}
              </p>
            </div>
          </div>
          {pushSupported && pushPermission !== 'denied' && (
            prefs.enabled && pushSubscribed ? (
              <button onClick={deactivatePush} disabled={activating} data-testid="deactivate-push"
                className="px-4 py-2 rounded-lg text-xs font-semibold border transition-colors"
                style={{ borderColor: 'var(--border-color)', color: 'var(--text-muted)' }}>
                {activating ? <Loader2 size={14} className="animate-spin" /> : 'Desactivar'}
              </button>
            ) : (
              <button onClick={activatePush} disabled={activating} data-testid="activate-push"
                className="px-4 py-2 rounded-lg text-xs font-bold text-white"
                style={{ backgroundColor: 'var(--accent-primary)' }}>
                {activating ? <Loader2 size={14} className="animate-spin" /> : 'Activar notificaciones'}
              </button>
            )
          )}
          {pushPermission === 'denied' && (
            <span className="text-[10px] px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 font-medium">Bloqueado</span>
          )}
        </div>
      </div>

      {/* Alert Configuration */}
      <div className="rounded-xl border overflow-hidden mb-6" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}>
        {/* Suggestion alerts by category */}
        <div className="px-5 py-3 border-b" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--surface-secondary)' }}>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Alertas de sugerencias en llamada</p>
        </div>
        <div className="p-2 space-y-1">
          <AlertRow icon={AlertTriangle} color="#ef4444" label="Errores de datos" description="Cuando se detecta un error en los datos mencionados por el agente"
            checked={prefs.alert_error_dato} onChange={v => updatePref('alert_error_dato', v)} testId="toggle-alert-error" />
          <AlertRow icon={Target} color="#f59e0b" label="Oportunidades" description="Oportunidades de cross-sell, upsell u otras ventas detectadas"
            checked={prefs.alert_oportunidad} onChange={v => updatePref('alert_oportunidad', v)} testId="toggle-alert-opp" />
          <AlertRow icon={Lightbulb} color="#3b82f6" label="Consejos" description="Tips de coaching y tecnicas de venta"
            checked={prefs.alert_consejo} onChange={v => updatePref('alert_consejo', v)} testId="toggle-alert-tip" />
        </div>

        {/* By urgency */}
        <div className="px-5 py-3 border-t border-b" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--surface-secondary)' }}>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Filtro por urgencia</p>
        </div>
        <div className="p-2 space-y-1">
          <AlertRow icon={Zap} color="#ef4444" label="Urgencia alta" description="Solo las sugerencias mas criticas"
            checked={prefs.alert_urgency_high} onChange={v => updatePref('alert_urgency_high', v)} testId="toggle-urgency-high" />
          <AlertRow icon={Zap} color="#f59e0b" label="Urgencia media" description="Sugerencias de importancia moderada"
            checked={prefs.alert_urgency_medium} onChange={v => updatePref('alert_urgency_medium', v)} testId="toggle-urgency-medium" />
          <AlertRow icon={Zap} color="#64748b" label="Urgencia baja" description="Todas las sugerencias incluyendo las menores"
            checked={prefs.alert_urgency_low} onChange={v => updatePref('alert_urgency_low', v)} testId="toggle-urgency-low" />
        </div>

        {/* Other alerts */}
        <div className="px-5 py-3 border-t border-b" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--surface-secondary)' }}>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Otras alertas</p>
        </div>
        <div className="p-2 space-y-1">
          <AlertRow icon={TrendingDown} color="#ef4444" label="Score bajo de agente" description="Cuando el score de rendimiento baja del umbral"
            checked={prefs.alert_score_low} onChange={v => updatePref('alert_score_low', v)} testId="toggle-alert-score" />
          {prefs.alert_score_low && (
            <div className="flex items-center gap-3 px-4 py-2 ml-11">
              <label className="text-xs" style={{ color: 'var(--text-muted)' }}>Umbral:</label>
              <input type="number" min="0" max="100" value={prefs.score_threshold || 40}
                onChange={e => updatePref('score_threshold', parseInt(e.target.value) || 40)} data-testid="score-threshold"
                className="w-16 px-2 py-1 rounded border text-sm text-center font-mono outline-none"
                style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
              <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>puntos (0-100)</span>
            </div>
          )}
          <AlertRow icon={FileText} color="#8b5cf6" label="Sesion completada" description="Resumen cuando se finaliza una llamada"
            checked={prefs.alert_session_completed} onChange={v => updatePref('alert_session_completed', v)} testId="toggle-alert-session" />
          <AlertRow icon={Phone} color="#22c55e" label="Llamada Twilio entrante" description="Cuando se conecta una nueva llamada telefonica"
            checked={prefs.alert_twilio_incoming} onChange={v => updatePref('alert_twilio_incoming', v)} testId="toggle-alert-twilio" />
          <AlertRow icon={Calendar} color="#64748b" label="Resumen diario" description="Digest con metricas del dia anterior"
            checked={prefs.alert_daily_digest} onChange={v => updatePref('alert_daily_digest', v)} testId="toggle-alert-digest" />
        </div>
      </div>

      {/* Save */}
      <div className="flex items-center justify-between">
        <button onClick={savePrefs} disabled={saving} data-testid="save-notification-prefs"
          className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold text-white"
          style={{ backgroundColor: 'var(--accent-primary)' }}>
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Guardar preferencias
        </button>
        <div className="flex items-center gap-1.5 text-[10px]" style={{ color: 'var(--text-muted)' }}>
          <Info size={12} />
          <span>Las notificaciones requieren que el navegador este abierto o en segundo plano</span>
        </div>
      </div>
    </div>
  );
}
